import type * as pf from '@amirada/lib-shared'
import { NodeType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import type { TabsItem } from '#ui/types'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useDetailStore } from '@base/stores/detailStore'
import { ut_buildParamData, ut_navigateNewRoute, ut_psmName } from '@base/utils/utils'

export interface IDetailProps {
  // A node OUTSIDE the current navigation. Set → nobody has loaded it, so this composable fetches it
  // (see the watcher below). Leave it unset for the navigated node: passing paramData.namePath here
  // would freeze the path at setup time instead of following the route.
  objPath?: string
  // Belongs to objPath: does that path end at a configuration or at a node below one? It decides how
  // the path is split into conf/nodeName and cannot be read off the path itself.
  isConfig?: boolean
}
export function useDetail(p: IDetailProps) {
  const indexStore = useAppIndexStore()
  const detailStore = useDetailStore()

  // The paramData this composable reads and fetches under. One function for both, so the cache key
  // is computed in a single place.
  function buildParamData(): tp.IParamData {
    return ut_buildParamData(indexStore.state.paramData, { objPath: p.objPath })
  }

  // Fetch what the navigation did not: callInitAction loads the node it navigated to, and an objPath
  // asks for a different one. Without objPath this does nothing — the navigation has already loaded
  // that node, and a second loader here could only duplicate its request.
  // The watcher is registered unconditionally and gated inside: objPath is a prop and may arrive
  // after setup, and a gate around the watcher would then never register it.
  async function load(): Promise<void> {
    if (!p.objPath) { return }
    await detailStore.callDetailData(buildParamData(), !!p.isConfig)
  }
  watch(() => p.objPath, () => { void load() }, { immediate: true })

  /* onMounted(async() => {
    console.log('detail onMounted')
  })

  onBeforeMount(async() => {
    console.log('detail onBeforeMount')
  })

  onUpdated(async() => {
    console.log('detail onUpdated')
  })

  onUnmounted(async() => {
    console.log('detail onUnmounted')
  }) */

  const selectedTab: Ref<string> = ref('')
  const selectedTab2: Ref<string> = ref('')
  const detailTabs2: Ref<{ [value: string]: TabsItem[] }> = ref({}) // Get tabs2 as UI array

  //  Setup include fetching SEO data
  const detailSetup = computed((): boolean => {
    // console.log ('compute detailSetup');
    if (detailData.value && detailTabs.value && detailTabs2.value) {
      // Set SEO data
      const headElements: tp.IHeadElements = {
        title: detailData.value.desc,
        description: detailData.value.desc,
      }
      indexStore.setHeadElements(headElements)

      const openGraphElements: tp.IOpenGraphElements = {
        title: detailData.value.desc,
        description: detailData.value.desc,
      }
      indexStore.setOpenGraphElements(openGraphElements)

      // Activate selected tabs
      selectedTab.value = indexStore.state.paramData.tab || detailTabs?.value[0]?.value?.toString() || ''
      selectedTab2.value = getTab2()

      return true
    }
    else {
      return false
    }
  })

  /*
   * The detail data as the store holds it — nothing added, nothing copied.
   *
   * Until 01.09.2026 this inserted the product-list tab (a node that exists in no database: a
   * configuration has one exactly when it has products) and therefore had to DEEP COPY the whole
   * detail tree first, on every recompute, so the insertion would not reach the store. The server
   * answers that question now — it has just counted the list in the same batch — and with the
   * insertion the copy went too.
   */
  const detailData = computed((): pf.TPfNode | undefined => {
    return detailStore.getDetailData(buildParamData())
  })

  // Get tabs as UI array
  const detailTabs = computed((): TabsItem[] => {
    // console.log("compute detailTabs")
    const res: TabsItem[] = []
    const detail = detailData
    if (detail?.value?.nodes) {
      if (detail.value.nodes[0]?.nodeType === NodeType.nt_Tabulator) {
        for (const item of detail.value.nodes) {
          res.push ({
            label: item.desc,
            value: tabName(item),
          })
          // Check
          if (item.nodes) {
            const res2: TabsItem[] = []
            let hasSubTabs: boolean = false
            for (const item2 of item.nodes) {
              if (item2.nodeType === NodeType.nt_Tabulator) {
                hasSubTabs = true
                res2.push ({
                  label: item2.desc,
                  value: tabName(item2),
                })
              }
            }
            if (hasSubTabs) {
              detailTabs2.value[tabName(item)] = res2
            }
          }
        }
      }
    }
    return res
  })

  function changeTab() {
    selectedTab2.value = getTab2()
    ut_navigateNewRoute('', { tab: selectedTab.value, tab2: '' }, '', false)
  }

  function changeTab2() {
    ut_navigateNewRoute('', { tab2: selectedTab2.value }, '', false)
  }

  // const selectedTab2: Ref<string> = ref(getTab2());
  function getTab2() {
    let res: string = ''
    if (indexStore.state.paramData.tab2) {
      res = indexStore.state.paramData.tab2
    }
    else {
      const tab2 = detailTabs2?.value[selectedTab.value]
      if (tab2) {
        if (tab2[0]) {
          res = tab2[0].value?.toString() || ''
        }
      }
    }
    return res
  }

  function tabName(node: pf.TPfNode) {
    return ut_psmName(node)
  }

  return {
    indexStore,
    load,
    detailSetup,
    detailData,
    detailTabs,
    detailTabs2,
    selectedTab: ref(selectedTab),
    selectedTab2: ref(selectedTab2),
    changeTab,
    changeTab2,
    tabName,
  }
}
